import React, { useMemo } from "react";
import { Input, InputNumber, Select } from "antd";

import { MessageContent } from "../../../../flatbuffers/generated/ln2/message-content";
import { ControllerInfoContent } from "../../../../flatbuffers/generated/controller-info/controller-info-content";

import {
  type ControllerInfoContentDraft,
  type PayloadDraft,
} from "../../../../flatbuffers/builders/channelModifierPayloadBuilder";

// ---------------------------------------------------------------------------
// Module-scoped option lists — enum → { label, value }[] computed once.
// ---------------------------------------------------------------------------

function enumOptions<T extends Record<string, string | number>>(
  e: T,
): { label: string; value: number }[] {
  return Object.entries(e)
    .filter(([, v]) => typeof v === "number")
    .map(([label, value]) => ({ label, value: value as number }));
}

const CONTROLLER_INFO_CONTENT_OPTIONS = enumOptions(
  ControllerInfoContent as unknown as Record<string, string | number>,
).filter((o) => o.value !== ControllerInfoContent.NONE);

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const rowStyle: React.CSSProperties = { marginBottom: 8 };
const sectionStyle: React.CSSProperties = {
  marginTop: 4,
  padding: 8,
  border: "1px solid rgba(255, 255, 255, 0.18)",
  borderRadius: 4,
};
const fullWidth: React.CSSProperties = { width: "100%", marginTop: 4 };

// ---------------------------------------------------------------------------
// Per-MessageContent editors
// ---------------------------------------------------------------------------

function defaultControllerInfoContent(type: ControllerInfoContent): ControllerInfoContentDraft {
  switch (type) {
    case ControllerInfoContent.FloorLabelMessage:
      return { type, floorLabels: [] };
    case ControllerInfoContent.FirmwareVersionMessage:
      return { type, major: 0, minor: 0, revision: 0 };
    case ControllerInfoContent.ControllerLabelsMessage:
      return { type, controllerLabels: "" };
    case ControllerInfoContent.FloorLabelsDataMessage:
      return { type, floorLabelData: "" };
    case ControllerInfoContent.DispatcherVersionMessage:
      return { type, version: "" };
    case ControllerInfoContent.ControllerVersionInfoMessage:
      return { type, car: 0, version: "" };
    case ControllerInfoContent.JobNameMessage:
      return { type, jobName: "" };
    case ControllerInfoContent.FloorPhysicalOpeningsMessage:
      return { type, frontOpenings: [], rearOpenings: [] };
    case ControllerInfoContent.HallCallEligibilityMessage:
      return {
        type,
        configIndex: 0,
        controllerIndex: 0,
        downAuxFront: [],
        downAuxRear: [],
        downMainFront: [],
        downMainRear: [],
        upAuxFront: [],
        upAuxRear: [],
        upMainFront: [],
        upMainRear: [],
      };
    case ControllerInfoContent.ShuttleFloorsMessage:
      return { type, car: 0, eligibleFloors: [], carCallDisposition: 0 };
    // NONE is filtered out of the options and no draft can carry it.
    case ControllerInfoContent.NONE:
      return { type: ControllerInfoContent.JobNameMessage, jobName: "" };
    default: {
      const exhaustive: never = type;
      throw new Error(`unreachable ControllerInfoContent variant: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function ControllerInfoSubEditor({
  content,
  onChange,
}: {
  content: ControllerInfoContentDraft;
  onChange: (next: ControllerInfoContentDraft) => void;
}): React.JSX.Element {
  const changeType = React.useCallback(
    (type: ControllerInfoContent) => onChange(defaultControllerInfoContent(type)),
    [onChange],
  );

  return (
    <div style={sectionStyle} data-testid="modifier-payload-controller-info-editor">
      <div style={rowStyle}>
        <span className="generalStyles__inputLabel">Sub-Message Type</span>
        <Select
          data-testid="modifier-payload-controller-info-type"
          style={fullWidth}
          options={CONTROLLER_INFO_CONTENT_OPTIONS}
          value={content.type}
          onChange={changeType}
        />
      </div>
      <ControllerInfoFieldsEditor content={content} onChange={onChange} />
    </div>
  );
}

function ControllerInfoFieldsEditor({
  content,
  onChange,
}: {
  content: ControllerInfoContentDraft;
  onChange: (next: ControllerInfoContentDraft) => void;
}): React.JSX.Element {
  switch (content.type) {
    case ControllerInfoContent.FloorLabelMessage:
      return (
        <CsvListEditor
          label="Floor labels (comma-separated)"
          values={content.floorLabels}
          onChange={(v) => onChange({ ...content, floorLabels: v })}
        />
      );
    case ControllerInfoContent.FirmwareVersionMessage:
      return (
        <>
          <NumberRow
            label="Major"
            value={content.major}
            onChange={(v) => onChange({ ...content, major: v })}
          />
          <NumberRow
            label="Minor"
            value={content.minor}
            onChange={(v) => onChange({ ...content, minor: v })}
          />
          <NumberRow
            label="Revision"
            value={content.revision}
            onChange={(v) => onChange({ ...content, revision: v })}
          />
        </>
      );
    case ControllerInfoContent.ControllerLabelsMessage:
      return (
        <TextRow
          label="Controller labels"
          value={content.controllerLabels}
          onChange={(v) => onChange({ ...content, controllerLabels: v })}
        />
      );
    case ControllerInfoContent.FloorLabelsDataMessage:
      return (
        <TextRow
          label="Floor label data"
          value={content.floorLabelData}
          onChange={(v) => onChange({ ...content, floorLabelData: v })}
        />
      );
    case ControllerInfoContent.DispatcherVersionMessage:
      return (
        <TextRow
          label="Version"
          value={content.version}
          onChange={(v) => onChange({ ...content, version: v })}
        />
      );
    case ControllerInfoContent.ControllerVersionInfoMessage:
      return (
        <>
          <NumberRow
            label="Car"
            value={content.car}
            onChange={(v) => onChange({ ...content, car: v })}
          />
          <TextRow
            label="Version"
            value={content.version}
            onChange={(v) => onChange({ ...content, version: v })}
          />
        </>
      );
    case ControllerInfoContent.JobNameMessage:
      return (
        <TextRow
          label="Job name"
          value={content.jobName}
          onChange={(v) => onChange({ ...content, jobName: v })}
        />
      );
    case ControllerInfoContent.FloorPhysicalOpeningsMessage:
      return (
        <>
          <BoolListRow
            label="Front openings"
            values={content.frontOpenings}
            onChange={(v) => onChange({ ...content, frontOpenings: v })}
          />
          <BoolListRow
            label="Rear openings"
            values={content.rearOpenings}
            onChange={(v) => onChange({ ...content, rearOpenings: v })}
          />
        </>
      );
    case ControllerInfoContent.HallCallEligibilityMessage:
      return (
        <>
          <NumberRow
            label="Config index"
            value={content.configIndex}
            onChange={(v) => onChange({ ...content, configIndex: v })}
          />
          <NumberRow
            label="Controller index"
            value={content.controllerIndex}
            onChange={(v) => onChange({ ...content, controllerIndex: v })}
          />
          <BoolListRow
            label="Down aux front"
            values={content.downAuxFront}
            onChange={(v) => onChange({ ...content, downAuxFront: v })}
          />
          <BoolListRow
            label="Down aux rear"
            values={content.downAuxRear}
            onChange={(v) => onChange({ ...content, downAuxRear: v })}
          />
          <BoolListRow
            label="Down main front"
            values={content.downMainFront}
            onChange={(v) => onChange({ ...content, downMainFront: v })}
          />
          <BoolListRow
            label="Down main rear"
            values={content.downMainRear}
            onChange={(v) => onChange({ ...content, downMainRear: v })}
          />
          <BoolListRow
            label="Up aux front"
            values={content.upAuxFront}
            onChange={(v) => onChange({ ...content, upAuxFront: v })}
          />
          <BoolListRow
            label="Up aux rear"
            values={content.upAuxRear}
            onChange={(v) => onChange({ ...content, upAuxRear: v })}
          />
          <BoolListRow
            label="Up main front"
            values={content.upMainFront}
            onChange={(v) => onChange({ ...content, upMainFront: v })}
          />
          <BoolListRow
            label="Up main rear"
            values={content.upMainRear}
            onChange={(v) => onChange({ ...content, upMainRear: v })}
          />
        </>
      );
    case ControllerInfoContent.ShuttleFloorsMessage:
      return (
        <>
          <NumberRow
            label="Car"
            value={content.car}
            onChange={(v) => onChange({ ...content, car: v })}
          />
          <BoolListRow
            label="Eligible floors"
            values={content.eligibleFloors}
            onChange={(v) => onChange({ ...content, eligibleFloors: v })}
          />
          <NumberRow
            label="Car-call disposition"
            value={content.carCallDisposition}
            onChange={(v) => onChange({ ...content, carCallDisposition: v })}
          />
        </>
      );
    default: {
      const exhaustive: never = content;
      throw new Error(`unreachable ControllerInfoContent variant: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function TextRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={rowStyle}>
      <span className="generalStyles__inputLabel">{label}</span>
      <Input style={fullWidth} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NumberRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  return (
    <div style={rowStyle}>
      <span className="generalStyles__inputLabel">{label}</span>
      <InputNumber style={fullWidth} value={value} onChange={(v) => onChange(v ?? 0)} />
    </div>
  );
}

function CsvListEditor({
  label,
  values,
  onChange,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
}): React.JSX.Element {
  return (
    <div style={rowStyle}>
      <span className="generalStyles__inputLabel">{label}</span>
      <Input
        style={fullWidth}
        value={values.join(",")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0),
          )
        }
        placeholder="floor1,floor2,floor3"
      />
    </div>
  );
}

function BoolListRow({
  label,
  values,
  onChange,
}: {
  label: string;
  values: boolean[];
  onChange: (v: boolean[]) => void;
}): React.JSX.Element {
  return (
    <div style={rowStyle}>
      <span className="generalStyles__inputLabel">{label}</span>
      <Input
        style={fullWidth}
        value={values.map((b) => (b ? "1" : "0")).join(",")}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
              .map((s) => s === "1" || s.toLowerCase() === "true"),
          )
        }
        placeholder="1,0,1,1 (comma-separated booleans)"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level PayloadEditor
// ---------------------------------------------------------------------------

export interface PayloadEditorProps {
  draft: PayloadDraft;
  onChange: (next: PayloadDraft) => void;
}

export default function PayloadEditor({ draft, onChange }: PayloadEditorProps): React.JSX.Element {
  const enumSection = useMemo(() => {
    // The leaf-enum picker that used to live here for every MessageContent
    // category — Status::Running, Control::CarCallUnlock, Traffic::CarCall,
    // etc. — is now redundant. The Target Message picker (the
    // FlatbufferMessagePicker upstream in StepChannelModifiers) already
    // specifies the leaf, and `payloadDraftFromLeaf` mirrors that choice
    // into the draft's category-specific field. Re-showing the same
    // dropdown here only invited the operator to second-guess their
    // earlier pick. We keep two payload-side editors, both of which carry
    // information the picker can't express:
    //
    //   1. Traffic's `position` field on the ElevatorTrafficMessage — the
    //      floor index the modifier emits. Runtime value transforms only
    //      handle Control-side position currently, so Traffic still needs
    //      a static value here.
    //   2. ControllerInfo's nested content union (FloorLabels[],
    //      FirmwareVersion {major, minor, revision}, etc.) — the picker
    //      only chooses which inner sub-message variant; the actual data
    //      payload still needs the operator to fill in.
    switch (draft.kind) {
      case MessageContent.Traffic_TrafficMessage:
        return draft.device === "Elevator" ? (
          <NumberRow
            label="Position (floor index)"
            value={draft.position}
            onChange={(v) => onChange({ ...draft, position: v })}
          />
        ) : null;
      case MessageContent.ControllerInfo_ControllerInfoMessage:
        return (
          <ControllerInfoSubEditor
            content={draft.content}
            onChange={(c) => onChange({ ...draft, content: c })}
          />
        );
      default:
        return null;
    }
  }, [draft, onChange]);

  // When the schema can answer every payload-side question on its own —
  // i.e. there's no Traffic position to set and no ControllerInfo content
  // to fill in — we hide the entire Payload section. Re-showing only the
  // Device Kind selector (which the FlatbufferMessagePicker already
  // hard-codes to Elevator for every supported category) would be the
  // same redundancy the operator just called out for the leaf-enum
  // picker.
  if (enumSection === null) return <></>;
  return (
    <div style={sectionStyle} data-testid="modifier-payload-status-editor">
      {enumSection}
    </div>
  );
}
